import { z } from 'zod';
import { UUIDSchema } from '../ids.js';
export const CredentialType = z.enum(['nfc_card', 'mobile_key', 'pin_code']);
export const CredentialStatus = z.enum(['pending', 'active', 'revoked']);
export const CredentialInventoryType = z.enum(['touch_pass']);
export const CredentialInventoryStatus = z.enum([
    'unallocated',
    'allocated',
    'assigned',
    'lost',
    'retired',
]);
export const CredentialInventoryEventType = z.enum([
    'synced',
    'allocated',
    'reclaimed',
    'assigned',
    'released',
    'marked_lost',
    'marked_retired',
    'restored',
]);
export const Credential = z.object({
    id: UUIDSchema,
    tenant_id: UUIDSchema,
    employee_id: UUIDSchema,
    type: CredentialType,
    status: CredentialStatus,
    card_token: z.string().nullable(),
    unifi_credential_id: z.string().nullable(),
    label: z.string().nullable(),
    created_at: z.string().datetime(),
});
export const IssueCredentialBody = z.object({
    type: CredentialType,
    card_token: z.string().max(120).optional(),
    pin_code: z
        .string()
        .trim()
        .regex(/^\d{4,8}$/, 'PIN code must be 4 to 8 numeric digits')
        .optional(),
    // UniFi controller touch pass IDs are controller-defined strings and are
    // not guaranteed to be UUID-shaped.
    touch_pass_id: z.string().trim().min(1).max(120).optional(),
    label: z.string().max(120).optional(),
});
export const EnrollBody = z.object({
    reader_id: z.string().min(1).max(120),
    ttl_seconds: z.number().int().min(15).max(600).default(120),
});
export const EnrollCompleteBody = z.object({
    enrollment_id: z.string().min(1).max(200),
    label: z.string().max(120).optional(),
});
export const NfcEnrollmentStartBody = z.object({
    reader_id: z.string().min(1).max(120),
    reset_ua_card: z.boolean().optional().default(false),
    ttl_seconds: z.number().int().min(15).max(600).default(120),
    label: z.string().max(120).optional(),
});
export const NfcEnrollmentCompleteBody = z.object({
    label: z.string().max(120).optional(),
});
export const ListCredentialsQuery = z.object({
    status: CredentialStatus.optional(),
    type: CredentialType.optional(),
    employee_id: UUIDSchema.optional(),
    q: z.string().trim().min(1).max(120).optional(),
    limit: z.coerce.number().int().min(1).max(250).default(100),
});
export const CredentialInventoryItem = z.object({
    id: UUIDSchema,
    type: CredentialInventoryType,
    external_id: z.string().min(1),
    display_name: z.string().nullable(),
    status: CredentialInventoryStatus,
    tenant_id: UUIDSchema.nullable(),
    credential_id: UUIDSchema.nullable(),
    assigned_employee_id: UUIDSchema.nullable(),
    billing_started_at: z.string().datetime().nullable(),
    billing_ended_at: z.string().datetime().nullable(),
    source_last_seen_at: z.string().datetime().nullable(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const ListCredentialInventoryQuery = z.object({
    tenant_id: UUIDSchema.optional(),
    status: CredentialInventoryStatus.optional(),
    type: CredentialInventoryType.optional(),
    limit: z.coerce.number().int().min(1).max(500).default(250),
});
export const AllocateCredentialInventoryBody = z.object({
    tenant_id: UUIDSchema,
    note: z.string().trim().max(500).optional(),
});
export const CredentialInventoryStatusBody = z.object({
    status: z.enum(['lost', 'retired', 'unallocated']),
    note: z.string().trim().max(500).optional(),
});
export const CredentialInventoryReportQuery = z.object({
    from: z.string().date().optional(),
    to: z.string().date().optional(),
    tenant_id: UUIDSchema.optional(),
    format: z.enum(['json', 'csv']).default('json'),
});
export const CredentialPolicy = z.object({
    touch_credential_enabled: z.boolean(),
    third_party_nfc_enabled: z.boolean(),
    pin_code_enabled: z.boolean(),
    touch_pass_management_url: z.string().trim().url().max(2048).nullable(),
    enrollment_reader_mode: z.enum(['any', 'specified']),
    enrollment_reader_id: z.string().trim().min(1).max(120).nullable(),
    enrollment_reader_name: z.string().trim().min(1).max(200).nullable(),
});
export const UpdateCredentialPolicyBody = CredentialPolicy.extend({
    pin_code_enabled: CredentialPolicy.shape.pin_code_enabled.optional(),
    touch_pass_management_url: CredentialPolicy.shape.touch_pass_management_url.optional(),
    enrollment_reader_mode: CredentialPolicy.shape.enrollment_reader_mode.optional(),
    enrollment_reader_id: CredentialPolicy.shape.enrollment_reader_id.optional(),
    enrollment_reader_name: CredentialPolicy.shape.enrollment_reader_name.optional(),
    reason: z.string().trim().min(1, 'reason required').max(500),
});
//# sourceMappingURL=credential.js.map